import {
  Credentials,
  getDeviceId,
  normalizeServerUrl,
  REMUX_SERVER_URL,
} from "../storage/credentials";
import { Platform } from "react-native";
import { getHlsSegmentLength } from "../player/hlsStartupExperiment";
import { productProfile } from "../productProfile";
import { buildHomeWatchRows, HomeWatchRows } from "../recommendations/watchRows";

export type JellyfinUser = {
  Id?: string;
  Name?: string;
  Policy?: Record<string, unknown> & {
    IsAdministrator?: boolean;
    IsDisabled?: boolean;
  };
  Configuration?: JellyfinUserConfiguration;
};
export type JellyfinUserConfiguration = Record<string, unknown> & {
  AudioLanguagePreference?: string | null;
  SubtitleLanguagePreference?: string | null;
  SubtitleMode?: "Default" | "Smart" | "Always" | "OnlyForced" | "None";
  PlayDefaultAudioTrack?: boolean;
  RememberAudioSelections?: boolean;
  RememberSubtitleSelections?: boolean;
  EnableNextEpisodeAutoPlay?: boolean;
  OrderedViews?: string[];
  LatestItemsExcludes?: string[];
  MyMediaExcludes?: string[];
};
export type JellyfinDisplayPreferences = Record<string, unknown> & {
  CustomPrefs: Record<string, string>;
};
export type JellyfinItem = {
  Id?: string;
  Name?: string;
  OriginalTitle?: string;
  Type?: string;
  CollectionType?: string;
  MediaType?: string;
  Locations?: string[];
  Overview?: string;
  ProductionYear?: number;
  PremiereDate?: string;
  EndDate?: string;
  CommunityRating?: number;
  RunTimeTicks?: number;
  SeriesName?: string;
  SeriesId?: string;
  ParentId?: string;
  OfficialRating?: string;
  Genres?: string[];
  Tags?: string[];
  ProviderIds?: Record<string, string>;
  ProductionLocations?: string[];
  Status?: string;
  GenreItems?: Array<{ Id?: string; Name?: string }>;
  ParentIndexNumber?: number;
  IndexNumber?: number;
  ChildCount?: number;
  Album?: string;
  AlbumId?: string;
  AlbumArtist?: string;
  AlbumPrimaryImageTag?: string;
  ArtistItems?: Array<{ Id?: string; Name?: string }>;
  UserData?: {
    PlaybackPositionTicks?: number;
    PlayedPercentage?: number;
    IsFavorite?: boolean;
    Played?: boolean;
    LastPlayedDate?: string;
  };
  ImageTags?: Record<string, string>;
  BackdropImageTags?: string[];
  ParentBackdropItemId?: string;
  ParentBackdropImageTags?: string[];
  Trickplay?: Record<string, Record<string, JellyfinTrickplayInfo>>;
  ImageBlurHashes?: Record<string, Record<string, string>>;
  People?: Array<{ Id?: string; Name?: string; Role?: string; Type?: string }>;
  Studios?: Array<{ Id?: string; Name?: string }>;
  ChannelId?: string;
  ChannelNumber?: string;
  StartDate?: string;
  CurrentProgram?: JellyfinItem;
  MediaSources?: Array<{
    Id?: string;
    Name?: string;
    Container?: string;
    Path?: string;
    DirectStreamUrl?: string;
    TranscodingUrl?: string;
    TranscodingSubProtocol?: string;
    TranscodingContainer?: string;
    SupportsDirectPlay?: boolean;
    SupportsDirectStream?: boolean;
    SupportsTranscoding?: boolean;
    RunTimeTicks?: number;
    Bitrate?: number;
    Size?: number;
    VideoCodec?: string;
    ContentHash?: string;
    ChapterSourceContentHash?: string;
    VirtualChapters?: boolean;
    ChaptersInherited?: boolean;
    Chapters?: Array<{
      Id?: number;
      Title?: string;
      StartPositionTicks?: number;
      EndPositionTicks?: number;
    }>;
    Remux?: {
      ProviderInfo?: Record<string, unknown>;
      PreProbed?: boolean;
    };
    MediaStreams?: Array<{
      Index?: number;
      Type?: string;
      Codec?: string;
      Language?: string;
      Title?: string;
      DisplayTitle?: string;
      IsDefault?: boolean;
      IsForced?: boolean;
      IsExternal?: boolean;
      Channels?: number;
      ChannelLayout?: string;
      BitRate?: number;
      SampleRate?: number;
      Width?: number;
      Height?: number;
      VideoRangeType?: string;
      DeliveryUrl?: string;
    }>;
  }>;
};

export type JellyfinTrickplayInfo = {
  Width?: number;
  Height?: number;
  TileWidth?: number;
  TileHeight?: number;
  ThumbnailCount?: number;
  Interval?: number;
  Bandwidth?: number;
};

export type JellyflixTrickplaySheet = {
  pass: "rough" | "precise";
  index: number;
  startMs: number;
  endMs: number;
  intervalMs: number;
  cols: number;
  rows: number;
  thumbWidth: number;
  thumbHeight: number;
  availableThumbs?: number;
  url: string;
};

export type JellyflixTrickplayManifest = {
  itemId: string;
  status: "missing" | "queued" | "rough_generating" | "precise_generating" | "ready" | "failed" | "cancelled";
  durationMs?: number;
  sheets?: JellyflixTrickplaySheet[];
};

export type PlayerPlaybackInfo = {
  PlaySessionId?: string;
  ErrorCode?: string;
  MediaSources?: JellyfinItem["MediaSources"];
};

export type JellyfinRemotePlayState = {
  PositionTicks?: number;
  CanSeek?: boolean;
  IsPaused?: boolean;
  IsMuted?: boolean;
  VolumeLevel?: number;
  MediaSourceId?: string;
};

export type JellyfinRemoteSession = {
  Id?: string;
  DeviceId?: string;
  DeviceName?: string;
  DeviceType?: string;
  Client?: string;
  UserId?: string;
  UserName?: string;
  IsActive?: boolean;
  SupportsMediaControl?: boolean;
  SupportsRemoteControl?: boolean;
  PlayableMediaTypes?: string[];
  SupportedCommands?: string[];
  Capabilities?: {
    SupportsMediaControl?: boolean;
    PlayableMediaTypes?: string[];
    SupportedCommands?: string[];
  };
  NowPlayingItem?: JellyfinItem;
  PlayState?: JellyfinRemotePlayState;
};

export type PlaybackTelemetryEvent = {
  playbackKey: string;
  event: string;
  elapsedMs?: number;
  itemId?: string;
  itemName?: string;
  seriesName?: string;
  sourceId?: string;
  sourceName?: string;
  deliveryClass?: string;
  errorCategory?: string;
  details?: Record<string, unknown>;
};
export type RecommendationType =
  | "SimilarToRecentlyPlayed"
  | "SimilarToLikedItem"
  | "HasDirectorFromRecentlyPlayed"
  | "HasActorFromRecentlyPlayed"
  | "HasLikedDirector"
  | "HasLikedActor"
  | "MatchesUserTaste"
  | "Popular"
  | "RecentlyAdded";
export type RecommendationMediaKind = "Movie" | "Series";
export type RecommendationTelemetryEvent = {
  sessionKey: string;
  event: "shelf-impression" | "item-opened" | "play-requested";
  categoryId: string;
  recommendationType: RecommendationType;
  baselineItemId?: string;
  baselineItemName?: string;
  mediaKind: RecommendationMediaKind;
  shelfPosition: number;
  itemId?: string;
  itemPosition?: number;
  shuffleSeed: number;
  feedId?: string;
  algorithmVersion?: number;
  profileMode?: HomeRecommendationProfileMode;
  personalization?: HomeRecommendationPersonalization;
};
export type HomeRecommendationProfileMode =
  | "cold_start"
  | "blended"
  | "personalized";
export type HomeRecommendationPersonalization = "generic" | "personalized";
export type HomeRecommendationRow = {
  CategoryId: string;
  Title: string;
  RecommendationType: RecommendationType;
  Personalization: HomeRecommendationPersonalization;
  MediaKind: RecommendationMediaKind;
  BaselineItemName?: string;
  BaselineItemId?: string;
  Rank: number;
  Items: JellyfinItem[];
};
export type HomeRecommendationsResponse = {
  AlgorithmVersion: number;
  FeedId: string;
  Seed: number;
  ProfileMode: HomeRecommendationProfileMode;
  Confidence: number;
  Rows: HomeRecommendationRow[];
};
export type JellyfinMediaSegment = {
  ItemId?: string;
  Type?: "Intro" | "Outro" | "Preview" | "Recap" | string;
  StartTicks?: number;
  EndTicks?: number;
};

type AuthResponse = { AccessToken?: string; User?: JellyfinUser };
type ItemsResponse = { Items?: JellyfinItem[]; TotalRecordCount?: number };
export type JellyfinItemsPage = { items: JellyfinItem[]; totalCount: number };
export type AdminSystemInfo = {
  ServerName?: string;
  Version?: string;
  OperatingSystemDisplayName?: string;
  OperatingSystem?: string;
  SystemArchitecture?: string;
  HasPendingRestart?: boolean;
  StartupWizardCompleted?: boolean;
  ProgramDataPath?: string;
  CachePath?: string;
};
export type AdminSession = {
  Id?: string;
  UserName?: string;
  Client?: string;
  DeviceName?: string;
  LastActivityDate?: string;
  NowPlayingItem?: JellyfinItem;
  PlayState?: { IsPaused?: boolean; PositionTicks?: number };
  TranscodingInfo?: { IsVideoDirect?: boolean; IsAudioDirect?: boolean };
};
export type AdminUser = JellyfinUser & {
  LastLoginDate?: string;
  LastActivityDate?: string;
};
export type AdminTask = {
  Id?: string;
  Key?: string;
  Name?: string;
  State?: string;
  CurrentProgressPercentage?: number;
  LastExecutionResult?: { Status?: string; EndTimeUtc?: string };
};
export type AdminActivity = {
  Id?: number | string;
  Name?: string;
  Overview?: string;
  Type?: string;
  Date?: string;
  UserId?: string;
  Severity?: string;
};
export type AdminOverviewData = {
  system: AdminSystemInfo;
  sessions: AdminSession[];
  users: AdminUser[];
  libraries: JellyfinItem[];
  tasks: AdminTask[];
  activity: AdminActivity[];
  counts: Record<string, number>;
};
type QuickConnectResult = {
  Code?: string;
  Secret?: string;
  Authenticated?: boolean;
};
export type DiscordAuthInitiateResult = {
  secret: string;
  code: string;
  authorizeUrl: string;
  expiresIn: number;
};
export type DiscordIdentity = {
  provider: "discord";
  provider_user_id: string;
  provider_username: string;
};
type BackendInfo = { signupEnabled?: boolean };
export type RecommendationCategory = {
  CategoryId?: string;
  RecommendationType: RecommendationType;
  BaselineItemName?: string;
  BaselineItemId?: string;
  Items?: JellyfinItem[];
  MediaKind: RecommendationMediaKind;
};
type FiltersResponse = { Tags?: string[] };
// Web must stay on its serving origin so browsers do not reject Jellyflix's
// same-site backend policy. Native engines have no page origin and use the
// public absolute endpoint directly.
const JELLYFLIX_BACKEND_URL = Platform.OS === "web"
  ? "/api/jellyflix"
  : "https://jellyflix.app/api/jellyflix";

function requestHeaders(
  token?: string,
  deviceId = "jellyflix-renative",
): Record<string, string> {
  const clientAuthorization = `MediaBrowser Client="Jellyflix", Device="Native", DeviceId="${deviceId}", Version="0.1.0"`;
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: token
      ? `${clientAuthorization}, Token="${token}"`
      : clientAuthorization,
    ...(token ? { "X-Emby-Token": token } : {}),
  };
}

async function request<T>(
  serverUrl: string,
  path: string,
  init: RequestInit = {},
  token?: string,
  deviceId?: string,
): Promise<T> {
  const response = await fetch(`${normalizeServerUrl(serverUrl)}${path}`, {
    ...init,
    headers: { ...requestHeaders(token, deviceId), ...(init.headers ?? {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      typeof data?.error === "string"
        ? data.error
        : typeof data?.detail === "string"
          ? data.detail
        : typeof data?.Message === "string"
          ? data.Message
          : "Jellyflix request failed.";
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return data as T;
}

export async function authenticate(
  serverUrl: string,
  username: string,
  password: string,
): Promise<Credentials> {
  const deviceId = await getDeviceId();
  const result = await request<AuthResponse>(
    serverUrl,
    "/Users/AuthenticateByName",
    {
      method: "POST",
      body: JSON.stringify({ Username: username, Pw: password }),
    },
    undefined,
    deviceId,
  );
  const userId = result.User?.Id ?? "";
  const accessToken = result.AccessToken ?? "";
  if (!userId || !accessToken)
    throw new Error("The server returned an incomplete login response.");
  return { serverUrl: normalizeServerUrl(serverUrl), userId, accessToken, deviceId };
}

export async function getBackendInfo(): Promise<BackendInfo> {
  if (!productProfile.privateBackendEnabled) throw new Error("The private Jellyflix backend is unavailable in this build.");
  const response = await fetch(`${JELLYFLIX_BACKEND_URL}/server`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    throw new Error("Could not load Jellyflix server information.");
  return response.json() as Promise<BackendInfo>;
}

export async function signupAccount(
  username: string,
  password: string,
): Promise<void> {
  if (!productProfile.privateBackendEnabled) throw new Error("Signup is unavailable in this build.");
  const response = await fetch(`${JELLYFLIX_BACKEND_URL}/signup`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ serverUrl: REMUX_SERVER_URL, username, password }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(
      typeof data?.error === "string" ? data.error : "Signup failed.",
    );
}

export async function initiateQuickConnect(serverUrl: string): Promise<QuickConnectResult> {
  const deviceId = await getDeviceId();
  return request<QuickConnectResult>(
    serverUrl,
    "/QuickConnect/Initiate",
    { method: "POST" },
    undefined,
    deviceId,
  );
}

export async function getQuickConnectStatus(
  serverUrl: string,
  secret: string,
): Promise<QuickConnectResult> {
  const deviceId = await getDeviceId();
  return request<QuickConnectResult>(
    serverUrl,
    `/QuickConnect/Connect?Secret=${encodeURIComponent(secret)}`,
    {},
    undefined,
    deviceId,
  );
}

export async function authenticateQuickConnect(
  serverUrl: string,
  secret: string,
): Promise<Credentials> {
  const deviceId = await getDeviceId();
  const result = await request<AuthResponse>(
    serverUrl,
    "/Users/AuthenticateWithQuickConnect",
    { method: "POST", body: JSON.stringify({ Secret: secret }) },
    undefined,
    deviceId,
  );
  const userId = result.User?.Id ?? "";
  const accessToken = result.AccessToken ?? "";
  if (!userId || !accessToken)
    throw new Error("Quick Connect returned an incomplete login response.");
  return { serverUrl: normalizeServerUrl(serverUrl), userId, accessToken, deviceId };
}

export async function initiateDiscordAuth(
  serverUrl: string,
): Promise<DiscordAuthInitiateResult> {
  const deviceId = await getDeviceId();
  return request<DiscordAuthInitiateResult>(
    serverUrl,
    "/remux/auth/discord/initiate",
    { method: "POST" },
    undefined,
    deviceId,
  );
}

export async function initiateDiscordLink(
  credentials: Credentials,
): Promise<DiscordAuthInitiateResult> {
  return request<DiscordAuthInitiateResult>(
    credentials.serverUrl,
    "/remux/auth/discord/link/initiate",
    { method: "POST" },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function getDiscordIdentities(
  credentials: Credentials,
): Promise<DiscordIdentity[]> {
  return request<DiscordIdentity[]>(
    credentials.serverUrl,
    "/remux/auth/identities",
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function unlinkDiscord(credentials: Credentials): Promise<void> {
  await request(
    credentials.serverUrl,
    "/remux/auth/discord",
    { method: "DELETE" },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function getCurrentUser(
  credentials: Credentials,
): Promise<JellyfinUser> {
  return request<JellyfinUser>(
    credentials.serverUrl,
    `/Users/${encodeURIComponent(credentials.userId)}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function getUserConfiguration(credentials: Credentials) {
  const user = await getCurrentUser(credentials);
  if (!user.Configuration) throw new Error("User configuration was not returned.");
  return user.Configuration;
}

export async function updateUserConfiguration(
  credentials: Credentials,
  patch: Partial<JellyfinUserConfiguration>,
) {
  const current = await getUserConfiguration(credentials);
  await request<unknown>(
    credentials.serverUrl,
    `/Users/${encodeURIComponent(credentials.userId)}/Configuration`,
    { method: "POST", body: JSON.stringify({ ...current, ...patch }) },
    credentials.accessToken,
    credentials.deviceId,
  );
}

const JELLYFIN_WEB_DISPLAY_PREFERENCES_ID = "usersettings";
const JELLYFIN_WEB_DISPLAY_PREFERENCES_CLIENT = "emby";

/** Read the server-backed playback settings shared with Jellyfin Web. */
export async function getPlaybackDisplayPreferences(
  credentials: Credentials,
): Promise<JellyfinDisplayPreferences> {
  const query = new URLSearchParams({
    userId: credentials.userId,
    client: JELLYFIN_WEB_DISPLAY_PREFERENCES_CLIENT,
  });
  const result = await request<JellyfinDisplayPreferences>(
    credentials.serverUrl,
    `/DisplayPreferences/${JELLYFIN_WEB_DISPLAY_PREFERENCES_ID}?${query.toString()}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return { ...result, CustomPrefs: result.CustomPrefs ?? {} };
}

export async function updatePlaybackDisplayPreferences(
  credentials: Credentials,
  patch: Record<string, string>,
) {
  const current = await getPlaybackDisplayPreferences(credentials);
  const query = new URLSearchParams({
    userId: credentials.userId,
    client: JELLYFIN_WEB_DISPLAY_PREFERENCES_CLIENT,
  });
  await request<unknown>(
    credentials.serverUrl,
    `/DisplayPreferences/${JELLYFIN_WEB_DISPLAY_PREFERENCES_ID}?${query.toString()}`,
    {
      method: "POST",
      body: JSON.stringify({
        ...current,
        CustomPrefs: { ...current.CustomPrefs, ...patch },
      }),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function getUserViews(
  credentials: Credentials,
): Promise<JellyfinItem[]> {
  const result = await request<ItemsResponse>(
    credentials.serverUrl,
    `/Users/${encodeURIComponent(credentials.userId)}/Views`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return result.Items ?? [];
}

export async function getAdminOverview(
  credentials: Credentials,
): Promise<AdminOverviewData> {
  const [system, sessions, users, libraries, tasks, activityResult, counts] =
    await Promise.all(
    [
      request<AdminSystemInfo>(
    credentials.serverUrl,
        "/System/Info",
        {},
        credentials.accessToken,
        credentials.deviceId,
      ),
      request<AdminSession[]>(
    credentials.serverUrl,
        "/Sessions",
        {},
        credentials.accessToken,
        credentials.deviceId,
      ),
      request<AdminUser[]>(
    credentials.serverUrl,
        "/Users",
        {},
        credentials.accessToken,
        credentials.deviceId,
      ),
      request<JellyfinItem[]>(
    credentials.serverUrl,
        "/Library/VirtualFolders",
        {},
        credentials.accessToken,
        credentials.deviceId,
      ),
      request<AdminTask[]>(
    credentials.serverUrl,
        "/ScheduledTasks",
        {},
        credentials.accessToken,
        credentials.deviceId,
      ),
      request<{ Items?: AdminActivity[] }>(
    credentials.serverUrl,
        "/System/ActivityLog/Entries?Limit=8",
        {},
        credentials.accessToken,
        credentials.deviceId,
      ),
      request<Record<string, number>>(
    credentials.serverUrl,
        `/Items/Counts?UserId=${encodeURIComponent(credentials.userId)}`,
        {},
        credentials.accessToken,
        credentials.deviceId,
      ),
    ],
  );
  return {
    system,
    sessions,
    users,
    libraries,
    tasks,
    activity: activityResult.Items ?? [],
    counts,
  };
}

export async function getAdminSectionRecords(
  credentials: Credentials,
  section: string,
): Promise<Array<Record<string, unknown>>> {
  const endpoints: Record<string, string> = {
    settings: "/System/Configuration",
    branding: "/Branding/Configuration",
    playback: "/System/Configuration",
    "playback/transcoding": "/System/Configuration/encoding",
    "playback/resume": "/System/Configuration",
    "playback/streaming": "/System/Configuration",
    "playback/trickplay": "/System/Configuration/encoding",
    activity: "/System/ActivityLog/Entries?Limit=100",
    livetv: "/LiveTv/Channels?Limit=100&EnableImages=false",
    "livetv/recordings": "/LiveTv/Recordings?Limit=100&EnableImages=false",
    plugins: "/Plugins",
    "plugins/repositories": "/Repositories",
    networking: "/System/Configuration/networking",
    keys: "/Auth/Keys",
    backups: "/System/Configuration",
    logs: "/System/Logs",
  };
  const path = endpoints[section];
  if (!path) return [];
  const value = await request<unknown>(
    credentials.serverUrl,
    path,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  if (Array.isArray(value))
    return value.filter((entry): entry is Record<string, unknown> =>
      Boolean(entry && typeof entry === "object"),
    );
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (Array.isArray(record.Items))
      return record.Items.filter((entry): entry is Record<string, unknown> =>
        Boolean(entry && typeof entry === "object"),
      );
    return Object.entries(record).map(([Name, Value]) => ({ Name, Value }));
  }
  return [];
}

const ADMIN_CONFIGURATION_ENDPOINTS: Record<string, string> = {
  settings: "/System/Configuration",
  branding: "/Branding/Configuration",
  networking: "/System/Configuration/networking",
  playback: "/System/Configuration",
  "playback/transcoding": "/System/Configuration/encoding",
  "playback/resume": "/System/Configuration",
  "playback/streaming": "/System/Configuration",
  "playback/trickplay": "/System/Configuration/encoding",
};

export async function getAdminConfiguration(
  credentials: Credentials,
  section: string,
): Promise<Record<string, unknown>> {
  const path = ADMIN_CONFIGURATION_ENDPOINTS[section];
  if (!path) return {};
  const value = await request<unknown>(
    credentials.serverUrl,
    path,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export async function saveAdminConfiguration(
  credentials: Credentials,
  section: string,
  value: Record<string, unknown>,
): Promise<void> {
  const readPath = ADMIN_CONFIGURATION_ENDPOINTS[section];
  if (!readPath) throw new Error("This section is not editable.");
  const writePath =
    section === "branding"
      ? "/System/Configuration/Branding"
      : readPath;
  await runAdminPost(credentials, writePath, value);
}

export async function runAdminGet(
  credentials: Credentials,
  path: string,
): Promise<unknown> {
  const normalized = path.trim();
  if (!normalized.startsWith("/") || normalized.startsWith("//"))
    throw new Error("Enter an absolute Jellyfin API path.");
  return request<unknown>(
    credentials.serverUrl,
    normalized,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function runAdminPost(
  credentials: Credentials,
  path: string,
  body?: unknown,
): Promise<unknown> {
  const normalized = path.trim();
  if (!normalized.startsWith("/") || normalized.startsWith("//"))
    throw new Error("Enter an absolute Jellyfin API path.");
  return request<unknown>(
    credentials.serverUrl,
    normalized,
    {
      method: "POST",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function runAdminDelete(
  credentials: Credentials,
  path: string,
): Promise<unknown> {
  const normalized = path.trim();
  if (!normalized.startsWith("/") || normalized.startsWith("//"))
    throw new Error("Enter an absolute Jellyfin API path.");
  return request<unknown>(
    credentials.serverUrl,
    normalized,
    { method: "DELETE" },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function createAdminUser(
  credentials: Credentials,
  name: string,
  password: string,
): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Enter a user name.");
  await runAdminPost(credentials, "/Users/New", {
    Name: trimmed,
    Password: password,
  });
}

export async function updateAdminUserPassword(
  credentials: Credentials,
  userId: string,
  password: string,
): Promise<void> {
  if (!userId) throw new Error("This user does not have a server ID.");
  await runAdminPost(
    credentials,
    `/Users/Password?userId=${encodeURIComponent(userId)}`,
    { NewPw: password, ResetPassword: password.length === 0 },
  );
}

export async function changeOwnPassword(
  credentials: Credentials,
  userId: string,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  if (!userId) throw new Error("This user does not have a server ID.");
  await request<unknown>(
    credentials.serverUrl,
    `/users/${encodeURIComponent(userId)}/password`,
    {
      method: "POST",
      body: JSON.stringify({ CurrentPw: currentPassword, NewPw: newPassword }),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function addAdminLibrary(
  credentials: Credentials,
  name: string,
  collectionType: string,
  path: string,
): Promise<void> {
  const trimmedName = name.trim();
  const trimmedPath = path.trim();
  if (!trimmedName || !trimmedPath)
    throw new Error("Enter a library name and media path.");
  const query = [
    `name=${encodeURIComponent(trimmedName)}`,
    `collectionType=${encodeURIComponent(collectionType.trim() || "mixed")}`,
    `paths=${encodeURIComponent(trimmedPath)}`,
    "refreshLibrary=true",
  ].join("&");
  await runAdminPost(credentials, `/Library/VirtualFolders?${query}`, {});
}

export async function removeAdminLibrary(
  credentials: Credentials,
  name: string,
): Promise<void> {
  const query = `name=${encodeURIComponent(name)}&refreshLibrary=true`;
  await runAdminDelete(credentials, `/Library/VirtualFolders?${query}`);
}

export async function updateAdminUserPolicy(
  credentials: Credentials,
  user: AdminUser,
  patch: Record<string, unknown>,
): Promise<void> {
  if (!user.Id) throw new Error("This user does not have a server ID.");
  await runAdminPost(
    credentials,
    `/Users/${encodeURIComponent(user.Id)}/Policy`,
    { ...(user.Policy ?? {}), ...patch },
  );
}

export async function getItem(
  credentials: Credentials,
  itemId: string,
): Promise<JellyfinItem> {
  return request<JellyfinItem>(
    credentials.serverUrl,
    `/Users/${encodeURIComponent(credentials.userId)}/Items/${encodeURIComponent(itemId)}`
    + `?Fields=Genres,Studios,AlbumArtist,ArtistItems,People,Overview,Trickplay,MediaSources,ImageTags,PrimaryImageAspectRatio,BackdropImageTags,Tags,ProviderIds`
    + `&EnableImages=true&EnableImageTypes=Primary,Backdrop,Logo,Thumb&ImageTypeLimit=1`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function getControllableSessions(
  credentials: Credentials,
): Promise<JellyfinRemoteSession[]> {
  return request<JellyfinRemoteSession[]>(
    credentials.serverUrl,
    `/Sessions?ControllableByUserId=${encodeURIComponent(credentials.userId)}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function sendRemotePlay(
  credentials: Credentials,
  sessionId: string,
  itemIds: string[],
  options: {
    startPositionTicks?: number;
    mediaSourceId?: string;
    audioStreamIndex?: number;
    subtitleStreamIndex?: number;
    startIndex?: number;
  } = {},
): Promise<void> {
  const query = new URLSearchParams({
    ItemIds: itemIds.join(','),
    PlayCommand: 'PlayNow',
    ControllingUserId: credentials.userId,
  });
  if (options.startPositionTicks !== undefined) query.set('StartPositionTicks', String(options.startPositionTicks));
  if (options.mediaSourceId) query.set('MediaSourceId', options.mediaSourceId);
  if (options.audioStreamIndex !== undefined) query.set('AudioStreamIndex', String(options.audioStreamIndex));
  if (options.subtitleStreamIndex !== undefined) query.set('SubtitleStreamIndex', String(options.subtitleStreamIndex));
  if (options.startIndex !== undefined) query.set('StartIndex', String(options.startIndex));
  await request<unknown>(
    credentials.serverUrl,
    `/Sessions/${encodeURIComponent(sessionId)}/Playing?${query.toString()}`,
    { method: 'POST' },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function sendRemotePlaystate(
  credentials: Credentials,
  sessionId: string,
  command: 'Pause' | 'Unpause' | 'Stop' | 'Seek' | 'NextTrack' | 'PreviousTrack',
  seekPositionTicks?: number,
): Promise<void> {
  const query = new URLSearchParams({ ControllingUserId: credentials.userId });
  if (seekPositionTicks !== undefined) query.set('SeekPositionTicks', String(Math.max(0, Math.round(seekPositionTicks))));
  await request<unknown>(
    credentials.serverUrl,
    `/Sessions/${encodeURIComponent(sessionId)}/Playing/${encodeURIComponent(command)}?${query.toString()}`,
    { method: 'POST' },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function sendRemoteCommand(
  credentials: Credentials,
  sessionId: string,
  name: string,
  arguments_: Record<string, string> = {},
): Promise<void> {
  await request<unknown>(
    credentials.serverUrl,
    `/Sessions/${encodeURIComponent(sessionId)}/Command`,
    {
      method: 'POST',
      body: JSON.stringify({
        Name: name,
        ControllingUserId: credentials.userId,
        Arguments: arguments_,
      }),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function getPlaybackSources(
  credentials: Credentials,
  itemId: string,
): Promise<NonNullable<JellyfinItem["MediaSources"]>> {
  const result = await request<{ MediaSources?: JellyfinItem["MediaSources"] }>(
    credentials.serverUrl,
    `/Items/${encodeURIComponent(itemId)}/PlaybackInfo?UserId=${encodeURIComponent(credentials.userId)}`,
    { method: "POST", body: "{}" },
    credentials.accessToken,
    credentials.deviceId,
  );
  return result.MediaSources ?? [];
}

const NATIVE_DEVICE_PROFILE = {
  Name: "Jellyflix Native",
  MaxStreamingBitrate: 120_000_000,
  DirectPlayProfiles: [
    {
      Container: "mp4,m4v,mov,mkv",
      Type: "Video",
      VideoCodec: "h264,hevc,h265,av1",
      AudioCodec: "aac,mp3,ac3,eac3,flac,dts,dtshd",
    },
  ],
  // Media3 renders embedded and sidecar text subtitles (SRT/SSA/WebVTT/TX3G)
  // from MP4 and MKV. Image subtitles (PGS/VobSub/DVB) stay unsupported so
  // the server keeps burning them through the transcode path instead of
  // silently dropping them on native.
  SubtitleProfiles: [
    { Format: "srt", Method: "Embed" },
    { Format: "srt", Method: "External" },
    { Format: "ass", Method: "Embed" },
    { Format: "ass", Method: "External" },
    { Format: "vtt", Method: "Embed" },
    { Format: "vtt", Method: "External" },
    { Format: "tx3g", Method: "Embed" },
  ],
  TranscodingProfiles: [
    {
      Container: "ts",
      Type: "Video",
      AudioCodec: "aac",
      VideoCodec: "h264,hevc,h265,av1",
      Context: "Streaming",
      Protocol: "hls",
      MaxAudioChannels: "8",
      MinSegments: 1,
      BreakOnNonKeyFrames: true,
    },
  ],
};

// Web-family engines negotiate only the common MSE baseline. Advertising
// native container/audio support here can make Remux copy E-AC-3/AC-3 or hand
// Chromium an MKV stream, after which hls.js rejects the manifest before any
// segment can play.
const WEB_DEVICE_PROFILE = {
  Name: "Jellyflix Web",
  MaxStreamingBitrate: 120_000_000,
  DirectPlayProfiles: [
    {
      Container: "mp4,m4v,mov",
      Type: "Video",
      VideoCodec: "h264,hevc,h265,av1",
      AudioCodec: "aac,mp3",
    },
  ],
  // hls.js creates TextTracks from HLS manifests — the browser can render
  // SRT/ASS/VTT text subtitles natively without server burn-in.
  SubtitleProfiles: [
    { Format: "srt", Method: "External" },
    { Format: "subrip", Method: "External" },
    { Format: "vtt", Method: "External" },
    { Format: "webvtt", Method: "External" },
    { Format: "ass", Method: "External" },
    { Format: "ssa", Method: "External" },
    { Format: "sup", Method: "External" },
    { Format: "pgs", Method: "External" },
    { Format: "pgssub", Method: "External" },
    { Format: "hdmv_pgs_subtitle", Method: "External" },
    { Format: "dvdsub", Method: "External" },
    { Format: "dvd_subtitle", Method: "External" },
    { Format: "dvbsub", Method: "External" },
  ],
  TranscodingProfiles: [
    {
      Container: "ts",
      Type: "Video",
      AudioCodec: "aac",
      VideoCodec: "h264,hevc,h265,av1",
      Context: "Streaming",
      Protocol: "hls",
      MaxAudioChannels: "2",
      MinSegments: 1,
      BreakOnNonKeyFrames: true,
    },
  ],
};

function getWebDeviceProfile() {
  const codecs = ["h264"];
  if (typeof document !== "undefined") {
    const video = document.createElement("video");
    if (video.canPlayType('video/mp4; codecs="hvc1.1.6.L93.B0"')) {
      codecs.push("hevc", "h265");
    }
    if (video.canPlayType('video/mp4; codecs="av01.0.05M.08"')) {
      codecs.push("av1");
    }
  }
  return {
    ...WEB_DEVICE_PROFILE,
    DirectPlayProfiles: WEB_DEVICE_PROFILE.DirectPlayProfiles.map((profile) => ({
      ...profile,
      VideoCodec: codecs.join(","),
    })),
  };
}

export async function getPlayerPlaybackInfo(
  credentials: Credentials,
  itemId: string,
  mediaSourceId?: string,
): Promise<PlayerPlaybackInfo> {
  return request<PlayerPlaybackInfo>(
    credentials.serverUrl,
    `/Items/${encodeURIComponent(itemId)}/PlaybackInfo?UserId=${encodeURIComponent(credentials.userId)}`,
    {
      method: "POST",
      body: JSON.stringify({
        UserId: credentials.userId,
        ...(mediaSourceId ? { MediaSourceId: mediaSourceId } : {}),
        DeviceProfile: Platform.OS === "web"
          ? getWebDeviceProfile()
          : NATIVE_DEVICE_PROFILE,
        EnableDirectPlay: true,
        EnableDirectStream: true,
        EnableTranscoding: true,
        AllowVideoStreamCopy: true,
        AllowAudioStreamCopy: Platform.OS !== "web",
      }),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function getMediaSegments(
  credentials: Credentials,
  itemId: string,
): Promise<JellyfinMediaSegment[]> {
  const result = await request<{ Items?: JellyfinMediaSegment[] }>(
    credentials.serverUrl,
    `/MediaSegments/${encodeURIComponent(itemId)}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return result.Items ?? [];
}

function absolutePlayerUrl(credentials: Credentials, value: string) {
  if (/^https?:\/\//i.test(value)) return value;
  return `${credentials.serverUrl}${value.startsWith("/") ? "" : "/"}${value}`;
}

function setPlayerQueryParams(
  rawUrl: string,
  entries: Array<string[] | undefined>,
) {
  const [path, rawQuery = ""] = rawUrl.split("?", 2);
  const replacements = entries.filter(
    (entry): entry is string[] => Boolean(entry),
  );
  const replacedKeys = new Set(replacements.map(([key]) => key.toLowerCase()));
  const retained = rawQuery
    .split("&")
    .filter(Boolean)
    .filter((part) => {
      const rawKey = part.split("=", 1)[0];
      try {
        return !replacedKeys.has(decodeURIComponent(rawKey).toLowerCase());
      } catch {
        return true;
      }
    });
  const encoded = replacements.map(
    ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
  );
  const query = [...retained, ...encoded].join("&");
  return `${path}${query ? `?${query}` : ""}`;
}

const prewarmLeaseBySession = new Map<string, number>();

export function isPrewarmLeaseUsable(playSessionId?: string) {
  if (!playSessionId) return false;
  const expiresAt = prewarmLeaseBySession.get(playSessionId) ?? 0;
  if (expiresAt <= Date.now()) {
    prewarmLeaseBySession.delete(playSessionId);
    return false;
  }
  return true;
}

export function getPlayerStreamUrl(
  credentials: Credentials,
  itemId: string,
  source: NonNullable<JellyfinItem["MediaSources"]>[number],
  options: {
    playSessionId?: string;
    audioStreamIndex?: number;
    startTimeTicks?: number;
  } = {},
) {
  const rawServerUrl = source.TranscodingUrl ?? source.DirectStreamUrl;
  // DirectPlay rung: the server accepted the source for this device profile
  // and produced no transcode/remux URL. Stream the file progressively and
  // keep every ffmpeg off the critical path. Client-side seek works through
  // range requests, so no StartTimeTicks is sent.
  const directPlay = !rawServerUrl && source.SupportsDirectPlay === true;
  let streamUrl = rawServerUrl
    ? absolutePlayerUrl(credentials, rawServerUrl)
    : directPlay
      ? `${credentials.serverUrl}/Videos/${encodeURIComponent(itemId)}/stream`
      : `${credentials.serverUrl}/Videos/${encodeURIComponent(itemId)}/master.m3u8`;

  // Remux/Jellyfin PlaybackInfo is the authority when it supplies a URL. Some
  // audio-only probe results still point at stream.aac/stream.ts; the canonical
  // HLS master route is the portable video-player endpoint in that case.
  const [path, existingQuery = ""] = streamUrl.split("?", 2);
  const audioOnlyEndpoint = /\/stream\.(aac|mp3|opus|flac|ogg|m4a)$/i.test(path);
  const nonHlsTs =
    /\/stream\.ts$/i.test(path) &&
    (source.TranscodingSubProtocol ?? "").toLowerCase() !== "hls";
  // Strip SubtitleStreamIndex from the server-supplied URL — subtitles are
  // always rendered client-side and must never be burned into the video.
  const cleanedQuery = existingQuery
    ? existingQuery.split("&").filter((p) => {
        const key = p.split("=", 1)[0]?.toLowerCase();
        return key !== "subtitlestreamindex";
      }).join("&")
    : "";
  streamUrl = `${audioOnlyEndpoint || nonHlsTs ? path.replace(/\/stream\.\w+$/i, "/master.m3u8") : path}${cleanedQuery ? `?${cleanedQuery}` : ""}`;

  return setPlayerQueryParams(streamUrl, [
    source.Id ? ["mediaSourceId", source.Id] : undefined,
    ["ApiKey", credentials.accessToken],
    ["deviceId", credentials.deviceId],
    directPlay ? ["static", "true"] : undefined,
    options.playSessionId ? ["playSessionId", options.playSessionId] : undefined,
    !directPlay && options.playSessionId
      ? ["SegmentLength", String(getHlsSegmentLength(options.playSessionId))]
      : undefined,
    options.audioStreamIndex !== undefined && Number.isFinite(options.audioStreamIndex)
      ? ["AudioStreamIndex", String(options.audioStreamIndex)]
      : undefined,
    // SubtitleStreamIndex is intentionally NOT sent — subtitles are always
    // rendered client-side (JASSUB/libbitsub on web, libass+ffmpeg on mpv)
    // and must never be burned into the video stream.
options.startTimeTicks !== undefined && Number.isFinite(options.startTimeTicks) && options.startTimeTicks > 0 && !directPlay
      ? ["StartTimeTicks", String(Math.floor(options.startTimeTicks))]
      : undefined,
    !rawServerUrl && !directPlay ? ["VideoCodec", "h264"] : undefined,
    !rawServerUrl && !directPlay ? ["AudioCodec", "aac"] : undefined,
    !rawServerUrl && !directPlay ? ["SegmentContainer", "mp4"] : undefined,
    !rawServerUrl && !directPlay ? ["MinSegments", "1"] : undefined,
    !rawServerUrl && !directPlay ? ["BreakOnNonKeyFrames", "true"] : undefined,
    !rawServerUrl && !directPlay ? ["RequireAvc", "false"] : undefined,
    !rawServerUrl && !directPlay ? ["TranscodingProtocol", "hls"] : undefined,
  ]);
}

/**
 * Warm the bounded beginning of the authoritative source. Direct play reads
 * 128 KiB; HLS asks Remux for a server-bounded startup buffer which pauses
 * after two normal segments until Player claims the same PlaySessionId.
 */
export async function prewarmPlayerSource(
  credentials: Credentials,
  itemId: string,
  source: NonNullable<JellyfinItem["MediaSources"]>[number],
  playSessionId?: string,
  startTimeTicks?: number,
  audioStreamIndex?: number,
  signal?: AbortSignal,
): Promise<boolean> {
  if (!productProfile.aggressiveOptimizationsEnabled) return false;
  const streamUrl = getPlayerStreamUrl(
    credentials,
    itemId,
    source,
    { playSessionId, startTimeTicks, audioStreamIndex },
  );
  const directPlay =
    !source.TranscodingUrl &&
    !source.DirectStreamUrl &&
    source.SupportsDirectPlay === true;
  const hls = /\.m3u8(?:\?|$)/i.test(streamUrl);
  if (!directPlay && !hls) return false;

  const response = await fetch(
    hls
      ? setPlayerQueryParams(streamUrl, [["Prewarm", "true"]])
      : streamUrl,
    {
      method: "GET",
      headers: directPlay
        ? { Range: "bytes=0-131071" }
        : { Accept: "application/vnd.apple.mpegurl" },
      signal,
    },
  );
  if (directPlay && response.status !== 206) {
    await response.body?.cancel?.().catch(() => undefined);
    return false;
  }
  if (!response.ok) return false;
  if (directPlay) await response.arrayBuffer();
  else {
    await response.text();
    if (playSessionId) {
      const serverLeaseSeconds = Number(
        response.headers.get("X-Remux-Prewarm-Lease-Seconds") ?? 90,
      );
      // The client deliberately expires its handoff 15 seconds before the
      // server lease. Clamp unknown/incorrect headers to the agreed 75-second
      // usable window.
      const usableMs = Math.max(
        0,
        Math.min(75_000, serverLeaseSeconds * 1_000 - 15_000),
      );
      if (usableMs > 0) {
        prewarmLeaseBySession.set(playSessionId, Date.now() + usableMs);
      }
    }
  }
  return true;
}

export async function reportPlaybackTelemetry(
  credentials: Credentials,
  event: PlaybackTelemetryEvent,
): Promise<void> {
  if (!productProfile.aggressiveOptimizationsEnabled) return;
  if (!event.playbackKey || !event.event) return;
  await request<void>(
    credentials.serverUrl,
    "/remux/telemetry/playback",
    {
      method: "POST",
      body: JSON.stringify(event),
      ...(Platform.OS === "web" ? { keepalive: true } : {}),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function reportRecommendationTelemetry(
  credentials: Credentials,
  event: RecommendationTelemetryEvent,
): Promise<void> {
  if (!productProfile.aggressiveOptimizationsEnabled) return;
  await request<void>(
    credentials.serverUrl,
    "/remux/telemetry/recommendation",
    {
      method: "POST",
      body: JSON.stringify(event),
      ...(Platform.OS === "web" ? { keepalive: true } : {}),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export function getAudioStreamUrl(
  credentials: Credentials,
  itemId: string,
) {
  const params = [
    ["UserId", credentials.userId],
    ["DeviceId", credentials.deviceId],
    ["ApiKey", credentials.accessToken],
    ["MaxStreamingBitrate", "150000000"],
  ]
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");
  return `${credentials.serverUrl}/Audio/${encodeURIComponent(itemId)}/universal?${params}`;
}

export async function reportPlayerState(
  credentials: Credentials,
  event: "start" | "progress" | "stop",
  value: {
    itemId: string;
    mediaSourceId?: string;
    playSessionId?: string;
    positionSeconds: number;
    paused: boolean;
    muted: boolean;
    playMethod?: "DirectPlay" | "DirectStream" | "Transcode";
  },
): Promise<void> {
  const positionSeconds = Number.isFinite(value.positionSeconds)
    ? Math.max(0, value.positionSeconds)
    : 0;
  const endpoint =
    event === "start"
      ? "/Sessions/Playing"
      : event === "stop"
        ? "/Sessions/Playing/Stopped"
        : "/Sessions/Playing/Progress";
  await request(
    credentials.serverUrl,
    endpoint,
    {
      method: "POST",
      body: JSON.stringify({
        ItemId: value.itemId,
        MediaSourceId: value.mediaSourceId,
        PlaySessionId: value.playSessionId,
        PositionTicks: Math.round(positionSeconds * 10_000_000),
        IsPaused: value.paused,
        IsMuted: value.muted,
        CanSeek: true,
        PlayMethod: value.playMethod ?? "DirectStream",
      }),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function stopActiveEncoding(
  credentials: Credentials,
  playSessionId: string,
): Promise<void> {
  if (!playSessionId) return;
  const query = [
    ["DeviceId", credentials.deviceId],
    ["PlaySessionId", playSessionId],
  ]
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");
  await request<void>(
    credentials.serverUrl,
    `/Videos/ActiveEncodings?${query}`,
    {
      method: "DELETE",
      ...(Platform.OS === "web" ? { keepalive: true } : {}),
    },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function getItems(
  credentials: Credentials,
  query: Record<string, string | number | boolean>,
): Promise<JellyfinItem[]> {
  return (await getItemsPage(credentials, query)).items;
}

export async function getItemsPage(
  credentials: Credentials,
  query: Record<string, string | number | boolean>,
): Promise<JellyfinItemsPage> {
  const requestQuery = { ...query };
  if (typeof requestQuery.Fields === "string") {
    const fields = new Set(requestQuery.Fields.split(",").map((field) => field.trim()).filter(Boolean));
    fields.add("Tags");
    fields.add("ProviderIds");
    requestQuery.Fields = [...fields].join(",");
  }
  const params = Object.entries(requestQuery)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
  const result = await request<ItemsResponse>(
    credentials.serverUrl,
    `/Users/${encodeURIComponent(credentials.userId)}/Items?${params}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return {
    items: result.Items ?? [],
    totalCount: result.TotalRecordCount ?? 0,
  };
}

export async function getLiveTvChannels(
  credentials: Credentials,
): Promise<JellyfinItem[]> {
  const pageSize = 100;
  const items: JellyfinItem[] = [];
  const seen = new Set<string>();
  for (let startIndex = 0; startIndex < 5_000; startIndex += pageSize) {
    const page = await getLiveTvChannelsPage(credentials, startIndex, pageSize);
    for (const item of page.items) {
      const key = item.Id ?? `${item.ChannelNumber ?? ""}:${item.Name ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push(item);
    }
    if (page.items.length < pageSize || items.length >= page.totalCount) break;
  }
  return items;
}

export async function getLiveTvChannelsPage(
  credentials: Credentials,
  startIndex = 0,
  limit = 100,
): Promise<{ items: JellyfinItem[]; totalCount: number }> {
  const result = await request<ItemsResponse>(
    credentials.serverUrl,
    `/LiveTv/Channels?StartIndex=${Math.max(0, startIndex)}&Limit=${Math.min(200, Math.max(1, limit))}&EnableImages=true&AddCurrentProgram=true&Fields=Overview,PrimaryImageAspectRatio,CurrentProgram&EnableTotalRecordCount=true`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return { items: result.Items ?? [], totalCount: result.TotalRecordCount ?? 0 };
}

export async function getLiveTvPrograms(
  credentials: Credentials,
  channelIds: string[],
  minStartDate: string,
  maxStartDate: string,
): Promise<JellyfinItem[]> {
  if (!channelIds.length) return [];
  const channelChunks = Array.from(
    { length: Math.ceil(channelIds.length / 50) },
    (_, index) => channelIds.slice(index * 50, index * 50 + 50),
  );
  const chunkResults = await Promise.all(channelChunks.map(async (chunk) => {
    const items: JellyfinItem[] = [];
    const pageSize = 500;
    for (let startIndex = 0; startIndex < 10_000; startIndex += pageSize) {
      const params = [
        `ChannelIds=${encodeURIComponent(chunk.join(","))}`,
        `MinStartDate=${encodeURIComponent(minStartDate)}`,
        `MaxStartDate=${encodeURIComponent(maxStartDate)}`,
        `StartIndex=${startIndex}`,
        `Limit=${pageSize}`,
        "EnableImages=true",
        "EnableTotalRecordCount=true",
        `UserId=${encodeURIComponent(credentials.userId)}`,
      ].join("&");
      const result = await request<ItemsResponse>(
        credentials.serverUrl,
        `/LiveTv/Programs?${params}`,
        {},
        credentials.accessToken,
        credentials.deviceId,
      );
      const page = result.Items ?? [];
      items.push(...page);
      if (page.length < pageSize || items.length >= (result.TotalRecordCount ?? 0)) break;
    }
    return items;
  }));
  const seen = new Set<string>();
  return chunkResults.flat().filter((item) => {
    const key = item.Id ?? `${item.ChannelId ?? ""}:${item.StartDate ?? ""}:${item.Name ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function getGenres(
  credentials: Credentials,
  includeItemTypes?: string,
  parentId?: string,
): Promise<JellyfinItem[]> {
  const itemTypes = includeItemTypes
    ? `&IncludeItemTypes=${encodeURIComponent(includeItemTypes)}`
    : "";
  const parent = parentId
    ? `&ParentId=${encodeURIComponent(parentId)}`
    : "";
  const result = await request<ItemsResponse>(
    credentials.serverUrl,
    `/Genres?UserId=${encodeURIComponent(credentials.userId)}&SortBy=SortName&SortOrder=Ascending${itemTypes}${parent}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return result.Items ?? [];
}

export async function getProviderTags(
  credentials: Credentials,
): Promise<string[]> {
  const filters = await request<FiltersResponse>(
    credentials.serverUrl,
    `/Items/Filters2?IncludeItemTypes=Movie%2CSeries&Recursive=true`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return filters.Tags ?? [];
}

export async function getSimilarItems(
  credentials: Credentials,
  itemId: string,
  limit = 20,
  startIndex = 0,
): Promise<JellyfinItem[]> {
  const result = await request<ItemsResponse>(
    credentials.serverUrl,
    `/Items/${encodeURIComponent(itemId)}/Similar?UserId=${encodeURIComponent(credentials.userId)}&Limit=${limit}&StartIndex=${startIndex}`
    + `&Fields=Overview,UserData,BackdropImageTags,ImageTags,PrimaryImageAspectRatio,ProductionYear,RunTimeTicks,ChildCount`
    + `&EnableImages=true&EnableImageTypes=Primary,Backdrop,Thumb&ImageTypeLimit=1`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return result.Items ?? [];
}

export async function getSeasons(
  credentials: Credentials,
  seriesId: string,
): Promise<JellyfinItem[]> {
  const result = await request<ItemsResponse>(
    credentials.serverUrl,
    `/Shows/${encodeURIComponent(seriesId)}/Seasons?UserId=${encodeURIComponent(credentials.userId)}`
    + `&Fields=Overview,UserData,BackdropImageTags,ImageTags,PrimaryImageAspectRatio`
    + `&EnableImages=true&EnableImageTypes=Primary,Backdrop,Thumb&ImageTypeLimit=1`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return result.Items ?? [];
}

export async function getEpisodes(
  credentials: Credentials,
  seriesId: string,
  seasonId: string,
): Promise<JellyfinItem[]> {
  const result = await request<ItemsResponse>(
    credentials.serverUrl,
    `/Shows/${encodeURIComponent(seriesId)}/Episodes?UserId=${encodeURIComponent(credentials.userId)}&SeasonId=${encodeURIComponent(seasonId)}`
    + `&Fields=Overview,UserData,BackdropImageTags,PrimaryImageAspectRatio,ImageTags,Tags,ProviderIds`
    + `&EnableImages=true&EnableImageTypes=Primary,Backdrop,Thumb&ImageTypeLimit=1`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return result.Items ?? [];
}

export async function getPersonFilmography(
  credentials: Credentials,
  personId: string,
): Promise<JellyfinItem[]> {
  return getItems(credentials, {
    PersonIds: personId,
    Recursive: true,
    IncludeItemTypes: "Movie,Series",
    Fields: "PrimaryImageAspectRatio,Overview,Genres",
    SortBy: "PremiereDate",
    SortOrder: "Descending",
    EnableImages: true,
    EnableUserData: true,
  });
}

export async function getMusicCollection(
  credentials: Credentials,
  item: JellyfinItem,
): Promise<JellyfinItem[]> {
  if (!item.Id) return [];
  if (item.Type === "Playlist") {
    const result = await request<ItemsResponse>(
    credentials.serverUrl,
      `/Playlists/${encodeURIComponent(item.Id)}/Items?UserId=${encodeURIComponent(credentials.userId)}&Fields=Genres%2CUserData%2CMediaSources`,
      {},
      credentials.accessToken,
      credentials.deviceId,
    );
    return (result.Items ?? []).filter((entry) => entry.Type === "Audio");
  }
  if (item.Type === "MusicArtist") {
    return getItems(credentials, {
      AlbumArtistIds: item.Id,
      Recursive: true,
      IncludeItemTypes: "MusicAlbum",
      Fields: "Genres,UserData,ChildCount",
      SortBy: "ProductionYear,SortName",
      SortOrder: "Descending,Ascending",
    });
  }
  return getItems(credentials, {
    ParentId: item.Id,
    Recursive: true,
    IncludeItemTypes: "Audio",
    Fields: "Genres,UserData,MediaSources,Album,AlbumId,ArtistItems",
    SortBy: "ParentIndexNumber,IndexNumber,SortName",
    SortOrder: "Ascending",
  });
}

export async function getMusicGenres(
  credentials: Credentials,
  limit = 30,
): Promise<JellyfinItem[]> {
  const result = await request<ItemsResponse>(
    credentials.serverUrl,
    `/MusicGenres?UserId=${encodeURIComponent(credentials.userId)}&Limit=${limit}&SortBy=SortName&SortOrder=Ascending`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  return result.Items ?? [];
}

function recommendationShuffleSeed(
  credentials: Credentials,
  type: "Movie" | "Series",
) {
  const now = new Date();
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const value = `${credentials.serverUrl}|${credentials.userId}|${type}|${day}`;
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

export function homeRecommendationSeed(credentials: Credentials) {
  const now = new Date();
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const value = `${credentials.serverUrl}|${credentials.userId}|home|${day}`;
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

export async function getHomeRecommendations(
  credentials: Credentials,
  options: {
    rowLimit?: number;
    itemLimit?: number;
    seed?: number;
    includeMovies?: boolean;
    includeSeries?: boolean;
  } = {},
): Promise<HomeRecommendationsResponse> {
  const params = [
    `UserId=${encodeURIComponent(credentials.userId)}`,
    `RowLimit=${Math.min(12, Math.max(1, options.rowLimit ?? 12))}`,
    `ItemLimit=${Math.min(12, Math.max(5, options.itemLimit ?? 12))}`,
    `Seed=${Math.max(0, Math.floor(options.seed ?? homeRecommendationSeed(credentials)))}`,
    `IncludeMovies=${options.includeMovies !== false}`,
    `IncludeSeries=${options.includeSeries !== false}`,
  ].join("&");
  const response = await request<HomeRecommendationsResponse>(
    credentials.serverUrl,
    `/remux/home/recommendations?${params}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  if (
    !response ||
    !Array.isArray(response.Rows) ||
    typeof response.FeedId !== "string" ||
    typeof response.AlgorithmVersion !== "number"
  ) {
    const error = new Error("Home recommendation feed is not supported.") as Error & { status?: number };
    error.status = 404;
    throw error;
  }
  return response;
}

export async function getRecommendations(
  credentials: Credentials,
  type: "Movie" | "Series",
  limit = 15,
): Promise<JellyfinItem[]> {
  const categoryLimit = Math.min(12, Math.max(4, Math.ceil(limit / 2) + 2));
  const expectedCategories = Math.max(1, Math.ceil(categoryLimit * 0.5));
  const itemLimit = Math.min(
    10,
    Math.max(5, Math.ceil(limit / expectedCategories) + 2),
  );
  const endpoint =
    type === "Movie" ? "/movies/recommendations" : "/shows/recommendations";
  const params = [
    `UserId=${encodeURIComponent(credentials.userId)}`,
    `CategoryLimit=${categoryLimit}`,
    `ItemLimit=${itemLimit}`,
    "Shuffle=true",
    `ShuffleSeed=${recommendationShuffleSeed(credentials, type)}`,
  ].join("&");
  const categories = await request<RecommendationCategory[]>(
    credentials.serverUrl,
    `${endpoint}?${params}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  const populated = categories.filter((category) =>
    Boolean(category.Items?.length),
  );
  const categoryWindow =
    populated.length > 0
      ? Math.min(
          limit,
          Math.ceil(limit / Math.max(1, Math.ceil(populated.length * 0.5))) + 1,
        )
      : 2;
  const seen = new Set<string>();
  const items: JellyfinItem[] = [];
  for (
    let itemIndex = 0;
    items.length < limit &&
    populated.some((category) => itemIndex < (category.Items?.length ?? 0));
    itemIndex += 1
  ) {
    for (const category of populated) {
      if (itemIndex >= categoryWindow) continue;
      const item = category.Items?.[itemIndex];
      if (!item?.Id || seen.has(item.Id)) continue;
      seen.add(item.Id);
      items.push(item);
      if (items.length >= limit) break;
    }
  }
  return items;
}

export async function getRecommendationShelves(
  credentials: Credentials,
  type: RecommendationMediaKind,
  categoryLimit: number,
  itemLimit: number,
  shuffleSeed: number,
): Promise<RecommendationCategory[]> {
  const endpoint =
    type === "Movie" ? "/movies/recommendations" : "/shows/recommendations";
  const params = [
    `UserId=${encodeURIComponent(credentials.userId)}`,
    `CategoryLimit=${Math.min(12, Math.max(1, categoryLimit))}`,
    `ItemLimit=${Math.min(12, Math.max(1, itemLimit))}`,
    "Shuffle=true",
    `ShuffleSeed=${Math.max(0, Math.floor(shuffleSeed))}`,
  ].join("&");
  const categories = await request<Array<Omit<RecommendationCategory, "MediaKind">>>(
    credentials.serverUrl,
    `${endpoint}?${params}`,
    {},
    credentials.accessToken,
    credentials.deviceId,
  );
  const itemIds = Array.from(
    new Set(
      categories.flatMap((category) =>
        (category.Items ?? []).flatMap((item) => (item.Id ? [item.Id] : [])),
      ),
    ),
  );
  const similarityOrder = itemIds.length
    ? await getItems(credentials, {
        Ids: itemIds.join(","),
        SortBy: "SimilarityScore",
        SortOrder: "Descending",
        Limit: itemIds.length,
        EnableImages: false,
        EnableUserData: false,
        EnableTotalRecordCount: false,
      })
    : [];
  const similarityRank = new Map(
    similarityOrder.flatMap((item, index) => (item.Id ? [[item.Id, index]] : [])),
  );
  return categories.map((category) => ({
    ...category,
    MediaKind: type,
    Items: [...(category.Items ?? [])].sort(
      (left, right) =>
        (similarityRank.get(left.Id ?? "") ?? Number.MAX_SAFE_INTEGER) -
        (similarityRank.get(right.Id ?? "") ?? Number.MAX_SAFE_INTEGER),
    ),
  }));
}

export async function getHomeWatchRows(
  credentials: Credentials,
  limit = 20,
): Promise<HomeWatchRows> {
  const common = `UserId=${encodeURIComponent(credentials.userId)}&Limit=${limit * 3}&Fields=PrimaryImageAspectRatio,ImageTags&EnableUserData=true&EnableImages=true&EnableImageTypes=Backdrop,Thumb,Primary&ImageTypeLimit=1&EnableTotalRecordCount=false`;
  const [resume, nextUp] = await Promise.all([
    request<ItemsResponse>(
    credentials.serverUrl,
      `/Users/${encodeURIComponent(credentials.userId)}/Items/Resume?${common}&IncludeItemTypes=Movie%2CEpisode`,
      {},
      credentials.accessToken,
      credentials.deviceId,
    ),
    request<ItemsResponse>(
    credentials.serverUrl,
      `/Shows/NextUp?${common}`,
      {},
      credentials.accessToken,
      credentials.deviceId,
    ),
  ]);
  return buildHomeWatchRows(resume.Items ?? [], nextUp.Items ?? [], limit);
}

export async function getContinueWatching(
  credentials: Credentials,
  limit = 20,
): Promise<JellyfinItem[]> {
  return (await getHomeWatchRows(credentials, limit)).combined;
}

export async function markItemPlayed(
  credentials: Credentials,
  itemId: string,
): Promise<void> {
  await request(
    credentials.serverUrl,
    `/Users/${encodeURIComponent(credentials.userId)}/PlayedItems/${encodeURIComponent(itemId)}`,
    { method: "POST" },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function markItemUnplayed(
  credentials: Credentials,
  itemId: string,
): Promise<void> {
  await request(
    credentials.serverUrl,
    `/Users/${encodeURIComponent(credentials.userId)}/PlayedItems/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function setItemFavorite(
  credentials: Credentials,
  itemId: string,
  favorite: boolean,
): Promise<void> {
  await request(
    credentials.serverUrl,
    `/Users/${encodeURIComponent(credentials.userId)}/FavoriteItems/${encodeURIComponent(itemId)}`,
    { method: favorite ? "POST" : "DELETE" },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function updateItemMetadata(
  credentials: Credentials,
  itemId: string,
  metadata: Partial<JellyfinItem>,
): Promise<void> {
  await request(
    credentials.serverUrl,
    `/Items/${encodeURIComponent(itemId)}`,
    { method: "POST", body: JSON.stringify(metadata) },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function refreshItemMetadata(
  credentials: Credentials,
  itemId: string,
): Promise<void> {
  await request(
    credentials.serverUrl,
    `/Items/${encodeURIComponent(itemId)}/Refresh?MetadataRefreshMode=FullRefresh&ImageRefreshMode=FullRefresh&ReplaceAllMetadata=false&ReplaceAllImages=false`,
    { method: "POST" },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export async function deleteItem(
  credentials: Credentials,
  itemId: string,
): Promise<void> {
  await request(
    credentials.serverUrl,
    `/Items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
    credentials.accessToken,
    credentials.deviceId,
  );
}

export function imageSource(
  credentials: Credentials,
  item: JellyfinItem,
  kind: "Primary" | "Backdrop" | "Logo" | "Thumb" = "Primary",
  maxWidth = 720,
  fillHeight?: number,
  quality = 90,
) {
  const id = item.Id ?? "";
  const size = fillHeight
    ? `fillWidth=${maxWidth}&fillHeight=${fillHeight}`
    : `maxWidth=${maxWidth}`;
  const imagePath = kind === "Backdrop" ? "Backdrop/0" : kind;
  return {
    uri: `${credentials.serverUrl}/Items/${encodeURIComponent(id)}/Images/${imagePath}?${size}&quality=${Math.min(100, Math.max(40, Math.round(quality)))}`,
    headers: { "X-Emby-Token": credentials.accessToken },
  };
}

export function trickplaySource(
  credentials: Credentials,
  itemId: string,
  width: number,
  imageIndex: number,
  mediaSourceId?: string,
) {
  return {
    uri: `${credentials.serverUrl}/Videos/${encodeURIComponent(itemId)}/Trickplay/${Math.max(1, Math.round(width))}/${Math.max(0, Math.floor(imageIndex))}.jpg?ApiKey=${encodeURIComponent(credentials.accessToken)}&MediaSourceId=${encodeURIComponent(mediaSourceId || itemId)}`,
  };
}

export async function getJellyflixTrickplayManifest(itemId: string) {
  if (!productProfile.privateBackendEnabled) throw new Error("Generated Jellyflix trickplay is unavailable in this build.");
  const response = await fetch(
    `${JELLYFLIX_BACKEND_URL}/trickplay/items/${encodeURIComponent(itemId)}/manifest`,
    { headers: { Accept: "application/json" } },
  );
  if (!response.ok) throw new Error(`Trickplay manifest failed (${response.status}).`);
  const payload = await response.json() as { manifest?: JellyflixTrickplayManifest };
  if (!payload.manifest) throw new Error("Trickplay manifest was empty.");
  return payload.manifest;
}

export async function startJellyflixTrickplayGeneration(
  itemId: string,
  payload: Record<string, unknown>,
) {
  if (!productProfile.privateBackendEnabled) throw new Error("Generated Jellyflix trickplay is unavailable in this build.");
  const response = await fetch(
    `${JELLYFLIX_BACKEND_URL}/trickplay/items/${encodeURIComponent(itemId)}/start`,
    {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) throw new Error(`Trickplay generation failed (${response.status}).`);
}

export function jellyflixTrickplaySource(url: string) {
  if (/^https?:\/\//i.test(url)) return { uri: url };
  const path = `${url.startsWith("/") ? "" : "/"}${url}`;
  return { uri: Platform.OS === "web" ? path : `https://jellyflix.app${path}` };
}
